import { useEffect, useMemo } from 'react';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { useCustomerGet } from '../../bff-api/customers/useCustomerGet';
import { useEstimatedDeliveryDate } from '../../bff-api/delivery/useEstimatedDeliveryDate';
import { useCustomerGeoLocationStore } from '../../bff-api/stores/useCustomerGeoLocationStore';
import { isBasketEmpty } from '../../bff-api/utils/basket-utils';
import { isServer } from '../../utils/utils';
import { useUserZipCode } from '../useUserZipCode';
import {
    getPageType,
    isNotEventTriggered,
    removeEventSensitiveFields,
    removePageDataSensitiveFields,
} from './gtm-utils';
import { GTMEventName } from './model/GTMEventName';
import type { Customer } from '../../bff-api/customers/model';
import type { Order } from '../../bff-api/orders/model';
import type { Product, Products } from '../../bff-api/products/model';
import type { RegisterCustomerDTO } from '../../pages/account/model/RegisterCustomerDTO';
import type { Category } from '../../pages/show/model/CategoryResponse';
import type { GTMPageData } from './model/GTMPageData';
import type { GTMAddToBagEvent } from './model/events/GTMAddToBagEvent';
import type { GTMEvent } from './model/events/GTMEvent';
import type { GTMRemoveFromBagEvent } from './model/events/GTMRemoveFromBagEvent';

export interface GTM {
    init: () => void;
    onAddToBag: (event: GTMAddToBagEvent) => void;
    onCategoryPageView: (category: Category) => void;
    onCheckout: () => void;
    onClickSearchSuggestion: (searchTerm: string, searchSuggestion: string) => void;
    onCompareProductsPageView: (products: Products) => void;
    onContinueToBilling: (user: Customer) => void;
    onOrderPurchase: (order: Order) => void;
    onPdpPageView: (product: Product) => void;
    onPlpPageView: (category: Category) => void;
    onRemoveProductFromBag: (event: GTMRemoveFromBagEvent) => void;
    onSearch: (searchTerm: string) => void;
    onPwaError: (pwaError: unknown) => void;
    onUserRegistration: (data: RegisterCustomerDTO) => void;
    updatePageData: (data: GTMPageData) => void;
}

const updatePageData = (data: GTMPageData): void => {
    if (isServer) {
        return;
    }

    const filteredData = removePageDataSensitiveFields(data);

    window.pageData = {
        isPwa: true,
        ...window.pageData,
        ...filteredData,
    };
};

const updateDataLayer = (event: GTMEvent): void => {
    if (isServer || !window.dataLayer) {
        return;
    }

    const filteredEvent = removeEventSensitiveFields(event);

    window.dataLayer.push(filteredEvent);
};

/**
 * Custom React hook to work with Google Tag Manager.
 */
const useGTM = (): GTM => {
    const { data: bag } = useBasket();
    const { data: customer } = useCustomerGet();
    const { data: store } = useCustomerGeoLocationStore();
    const { data: zipCode } = useUserZipCode();
    const { data = {} } = useEstimatedDeliveryDate(isBasketEmpty(bag) ? '' : zipCode);
    const { edd } = data;

    const buildUser = useMemo(
        () => (): Customer | undefined => ({
            ...customer,
            c_zipCode: zipCode,
            c_nearestStoreID: store?.id,
            c_heapUserID: window.heap?.userId,
        }),
        [customer, zipCode, store],
    );

    useEffect(() => {
        if (bag) {
            updatePageData({ bag });
        }
    }, [bag]);

    useEffect(() => {
        const user = buildUser();
        if (user) {
            updatePageData({
                user,
            });
        }
    }, [buildUser, zipCode, store]);

    const init = (): void => {
        if (isServer) {
            return;
        }

        const pageType = getPageType();
        const user = buildUser();

        updatePageData({
            pageType,
            edd,
            pageName: document.title,
            user,
            bag,
        });
    };

    const onAddToBag = (event: GTMAddToBagEvent): void => {
        updatePageData({ bag: event.bag });

        updateDataLayer(event);
    };

    const onCategoryPageView = (category: Category): void => {
        updatePageData({ category, isCategoryPage: true });

        updateDataLayer({
            event: GTMEventName.CATEGORY_PAGE_VIEW,
            category,
        });
    };

    const onCheckout = (): void => {
        updateDataLayer({
            event: GTMEventName.START_CHECKOUT,
        });
    };

    const onClickSearchSuggestion = (term: string, suggestionSelected: string): void => {
        updateDataLayer({
            event: GTMEventName.CLICK_SEARCH_SUGGESTION,
            search: {
                term,
                suggestionSelected,
            },
        });
    };

    const onCompareProductsPageView = (products: Products): void => {
        if (products.length && isNotEventTriggered(GTMEventName.COMPARE_PRODUCTS_PAGE_VIEW)) {
            updatePageData({ products });

            updateDataLayer({
                event: GTMEventName.COMPARE_PRODUCTS_PAGE_VIEW,
                products,
            });
        }
    };

    const onContinueToBilling = (user: Customer): void => {
        updateDataLayer({
            event: GTMEventName.ON_CONTINUE_TO_BILLING,
            user,
        });
    };

    const onPdpPageView = (product: Product): void => {
        updatePageData({ product });

        updateDataLayer({
            event: GTMEventName.PDP_PAGE_VIEW,
            product,
        });
    };

    const onPlpPageView = (category: Category): void => {
        updatePageData({ category, isPLP: true });

        updateDataLayer({
            event: GTMEventName.PLP_PAGE_VIEW,
            category,
        });
    };

    const onRemoveProductFromBag = (event: GTMRemoveFromBagEvent): void => {
        updatePageData({ bag: event.bag });

        updateDataLayer(event);
    };

    const onSearch = (term: string): void => {
        updateDataLayer({
            event: GTMEventName.SEARCH,
            search: {
                term,
            },
        });
    };

    const onPwaError = (pwaError: unknown): void => {
        updateDataLayer({
            event: GTMEventName.PWA_ERROR,
            pwaError,
        });
    };

    const onUserRegistration = ({ firstName, lastName, email }: RegisterCustomerDTO): void => {
        updatePageData({ user: { ...window.pageData?.user, firstName, lastName, email } });

        updateDataLayer({
            event: GTMEventName.USER_REGISTRATION,
            user: {
                firstName,
                lastName,
                email,
            },
        });
    };

    const onOrderPurchase = (order: Order): void => {
        updatePageData({ orders: [...(window.pageData?.orders || []), order] });

        updateDataLayer({
            event: GTMEventName.ORDER_PURCHASE,
            order,
        });
    };

    return {
        init,
        onAddToBag,
        onCategoryPageView,
        onClickSearchSuggestion,
        onCompareProductsPageView,
        onCheckout,
        onContinueToBilling,
        onOrderPurchase,
        onPdpPageView,
        onPlpPageView,
        onRemoveProductFromBag,
        onSearch,
        onPwaError,
        onUserRegistration,
        updatePageData,
    };
};

export { useGTM };
